import { useAuthStore } from '../store/useAuthStore';
import type { Permission, OrgRole } from '../lib/permissions';

export interface UsePermissionsResult {
  role: OrgRole | null;
  permissions: Permission[];
  can: (permission: Permission) => boolean;
  hasRole: (roles: OrgRole[]) => boolean;
  isSystemAdmin: boolean;
  isLoading: boolean;
}

/**
 * Returns the current user's role and permissions from the auth store.
 * Use `can()` for permission checks, `hasRole()` for role-level gates,
 * and `isSystemAdmin` for platform-level system admin checks.
 *
 * This is for UX-only gating. Backend authorization is the security boundary.
 */
export function usePermissions(): UsePermissionsResult {
  const { orgRole, permissions, isSystemAdmin, isLoading } = useAuthStore((s) => ({
    orgRole: s.orgRole,
    permissions: s.permissions,
    isSystemAdmin: s.isSystemAdmin,
    isLoading: s.isLoading,
  }));

  const role = orgRole as OrgRole | null;

  return {
    role,
    permissions,
    can: (permission: Permission) => permissions.includes(permission),
    hasRole: (roles: OrgRole[]) => role !== null && roles.includes(role),
    isSystemAdmin,
    isLoading,
  };
}
